import { LitElement, html, nothing, type CSSResultGroup, type TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';
import styles from './styles.js';

export type FormFieldType =
  | 'text' | 'password' | 'email' | 'search' | 'tel' | 'url'
  | 'number'
  | 'textarea'
  | 'checkbox'
  | 'radiobutton'
  | 'toggleswitch'
  | 'togglebutton'
  | 'dropdown'
  | 'listbox'
  | 'selectbutton'
  | 'colorpicker'
  | 'datepicker'
  | 'slider'
  | 'rating'
  | 'editor'
  | 'autocomplete';

export type FormFieldLabelPos = 'top' | 'left';

/**
 * uwc-form-field
 *
 * Generic form field wrapper for data-driven forms. Renders the correct
 * uwc-* input component based on `type`, with a linked label, optional
 * error message, and configurable label position.
 *
 * Set `type` to pick the control and pass any component-specific props
 * via the `config` JS property.
 *
 * @attr {'text'|'password'|'email'|'search'|'tel'|'url'|'number'|'textarea'|'checkbox'|'radiobutton'|'toggleswitch'|'togglebutton'|'dropdown'|'listbox'|'selectbutton'|'colorpicker'|'datepicker'|'slider'|'rating'|'editor'|'autocomplete'} type
 *   - Which form control to render. Default: text.
 * @attr {string}  label      - Label text. Shown above the control (or beside it when label-pos=left).
 * @attr {'top'|'left'} label-pos - Label placement. Default: top.
 * @attr {string}  error      - Validation error message rendered below the control.
 * @attr {boolean} required   - Appends a * to the label.
 * @attr {boolean} disabled   - Forwarded to the inner uwc-* component.
 * @attr {boolean} invalid    - Forwarded to the inner uwc-* component.
 * @attr {boolean} fluid      - Stretches the inner control to fill container width.
 *
 * @prop {Record<string, unknown>} config
 *   Props forwarded directly to the inner uwc-* component. Supports any
 *   property accepted by the target component (e.g. placeholder, options,
 *   min, max, step, variant, size, etc.).
 *
 * @fires uwc-change  - Bubbles from the inner component unchanged.
 * @fires uwc-input   - Bubbles from the inner component unchanged.
 *
 * @csspart label   - The <label> element (non-inline types only).
 * @csspart control - The inner uwc-* component.
 * @csspart error   - The error message span.
 *
 * @cssprop --uwc-ff-label-width - Label column width in left layout. Default: 9rem.
 * @cssprop --uwc-ff-label-align - Vertical alignment in left layout. Default: center.
 */
export class UwcFormField extends LitElement {

  static override styles: CSSResultGroup = [styles];

  // ── Core ──────────────────────────────────────────────────────────

  @property({ reflect: true }) type: FormFieldType = 'text';
  @property() label?: string;
  @property({ attribute: 'label-pos', reflect: true }) labelPos: FormFieldLabelPos = 'top';
  @property({ type: Object }) config: Record<string, unknown> = {};

  // ── Validation ────────────────────────────────────────────────────

  @property() error?: string;

  /** Auto-populated from a uwc-invalid event fired by the inner control. */
  @state() private _autoError = '';

  // ── Modifiers ─────────────────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) invalid  = false;
  @property({ type: Boolean, reflect: true }) fluid    = false;

  // ── IDs for ARIA wiring ───────────────────────────────────────────

  private readonly _labelId   = `uwc-ff-lbl-${Math.random().toString(36).slice(2, 8)}`;
  private readonly _controlId = `uwc-ff-ctl-${Math.random().toString(36).slice(2, 8)}`;
  private readonly _errorId   = `uwc-ff-err-${Math.random().toString(36).slice(2, 8)}`;

  // ── Helpers ───────────────────────────────────────────────────────

  private _tagForType(): string {
    switch (this.type) {
      case 'textarea':     return 'uwc-textarea';
      case 'number':       return 'uwc-inputnumber';
      case 'checkbox':     return 'uwc-checkbox';
      case 'radiobutton':  return 'uwc-radiobutton';
      case 'toggleswitch': return 'uwc-toggleswitch';
      case 'togglebutton': return 'uwc-togglebutton';
      case 'dropdown':     return 'uwc-dropdown';
      case 'listbox':      return 'uwc-listbox';
      case 'selectbutton': return 'uwc-selectbutton';
      case 'colorpicker':  return 'uwc-colorpicker';
      case 'datepicker':   return 'uwc-datepicker';
      case 'slider':       return 'uwc-slider';
      case 'rating':       return 'uwc-rating';
      case 'editor':       return 'uwc-editor';
      case 'autocomplete': return 'uwc-autocomplete';
      default:             return 'uwc-inputtext';
    }
  }

  /** Inline controls manage their own label text; external label is not shown. */
  private _isInlineControl(): boolean {
    return ['checkbox', 'radiobutton', 'toggleswitch', 'togglebutton'].includes(this.type);
  }

  // ── Lifecycle ─────────────────────────────────────────────────────

  override firstUpdated() {
    this.addEventListener('uwc-invalid', (e: Event) => {
      this._autoError = (e as CustomEvent).detail?.validationMessage ?? '';
    });
    this.addEventListener('uwc-input', () => {
      if (this._autoError) {
        const ctrl = this._getInnerControl();
        if (ctrl?.validity?.valid) this._autoError = '';
      }
    });
    this.addEventListener('uwc-change', () => {
      if (this._autoError) {
        const ctrl = this._getInnerControl();
        if (ctrl?.validity?.valid) this._autoError = '';
      }
    });
    // datepicker fires uwc-date-select instead of uwc-change
    this.addEventListener('uwc-date-select', () => {
      if (this._autoError) {
        const ctrl = this._getInnerControl();
        if (ctrl?.validity?.valid) this._autoError = '';
      }
    });
  }

  override updated() {
    this._syncControl();
  }

  private _getInnerControl(): (HTMLElement & { validity?: ValidityState }) | null {
    const tag = this._tagForType();
    return this.shadowRoot?.querySelector(tag) as (HTMLElement & { validity?: ValidityState }) | null;
  }

  private _syncControl() {
    const tag = this._tagForType();
    const el = this.shadowRoot?.querySelector(tag) as (HTMLElement & Record<string, unknown>) | null;
    if (!el) return;

    // Always-synced top-level modifiers
    (el as any).disabled = this.disabled;
    // Drive invalid from both the explicit `invalid` attr and auto-detected errors
    // so the inner component's visual state always reflects the overall error state.
    const activeErr = this.error || this._autoError;
    (el as any).invalid = this.invalid || !!activeErr;
    if ('fluid'    in el) (el as any).fluid    = this.fluid;
    if ('required' in el) (el as any).required = this.required;

    // uwc-inputtext needs its HTML input type set
    if (tag === 'uwc-inputtext') {
      (el as any).type = this.type;
    }

    // Inline controls receive the label text directly
    if (this._isInlineControl() && this.label && !('label' in this.config)) {
      (el as any).label = this.label;
    }

    // Forward all consumer-supplied config properties
    for (const [key, val] of Object.entries(this.config)) {
      (el as any)[key] = val;
    }

    // ARIA: link external label → control
    if (this.label && !this._isInlineControl()) {
      el.setAttribute('aria-labelledby', this._labelId);
    }

    // ARIA: validation state
    const activeError = this.error || this._autoError;
    if (activeError) {
      el.setAttribute('aria-invalid', 'true');
      el.setAttribute('aria-describedby', this._errorId);
    } else {
      el.removeAttribute('aria-invalid');
      el.removeAttribute('aria-describedby');
    }
  }

  // ── Handlers ─────────────────────────────────────────────────────

  private _onLabelClick() {
    const tag = this._tagForType();
    const el = this.shadowRoot?.querySelector(tag) as HTMLElement | null;
    // Delegate focus to the inner component; web components forward it internally
    el?.focus?.();
  }

  // ── Render ────────────────────────────────────────────────────────

  private _renderControl(): TemplateResult {
    const id  = this._controlId;
    const cls = 'uwc-ff__control';
    switch (this.type) {
      case 'textarea':     return html`<uwc-textarea     id=${id} part="control" class=${cls}></uwc-textarea>`;
      case 'number':       return html`<uwc-inputnumber  id=${id} part="control" class=${cls}></uwc-inputnumber>`;
      case 'checkbox':     return html`<uwc-checkbox     id=${id} part="control" class=${cls}></uwc-checkbox>`;
      case 'radiobutton':  return html`<uwc-radiobutton  id=${id} part="control" class=${cls}></uwc-radiobutton>`;
      case 'toggleswitch': return html`<uwc-toggleswitch id=${id} part="control" class=${cls}></uwc-toggleswitch>`;
      case 'togglebutton': return html`<uwc-togglebutton id=${id} part="control" class=${cls}></uwc-togglebutton>`;
      case 'dropdown':     return html`<uwc-dropdown     id=${id} part="control" class=${cls}></uwc-dropdown>`;
      case 'listbox':      return html`<uwc-listbox      id=${id} part="control" class=${cls}></uwc-listbox>`;
      case 'selectbutton': return html`<uwc-selectbutton id=${id} part="control" class=${cls}></uwc-selectbutton>`;
      case 'colorpicker':  return html`<uwc-colorpicker  id=${id} part="control" class=${cls}></uwc-colorpicker>`;
      case 'datepicker':   return html`<uwc-datepicker   id=${id} part="control" class=${cls}></uwc-datepicker>`;
      case 'slider':       return html`<uwc-slider       id=${id} part="control" class=${cls}></uwc-slider>`;
      case 'rating':       return html`<uwc-rating       id=${id} part="control" class=${cls}></uwc-rating>`;
      case 'editor':       return html`<uwc-editor       id=${id} part="control" class=${cls}></uwc-editor>`;
      case 'autocomplete': return html`<uwc-autocomplete id=${id} part="control" class=${cls}></uwc-autocomplete>`;
      default:             return html`<uwc-inputtext    id=${id} part="control" class=${cls}></uwc-inputtext>`;
    }
  }

  override render() {
    const showLabel = !!this.label && !this._isInlineControl();

    const labelEl = showLabel
      ? html`<label
          id=${this._labelId}
          part="label"
          class="uwc-ff__label"
          ?data-required=${this.required}
          @click=${this._onLabelClick}
        >${this.label}</label>`
      : nothing;

    const activeError = this.error || this._autoError;
    const errorEl = activeError
      ? html`<span
          id=${this._errorId}
          part="error"
          class="uwc-ff__error"
          role="alert"
          aria-live="polite"
        >${activeError}</span>`
      : nothing;

    return html`
      <div class="uwc-ff__wrapper">
        ${labelEl}
        <div class="uwc-ff__field">
          ${this._renderControl()}
          ${errorEl}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-form-field': UwcFormField; }
}
